import type { ArticleTileViewModel } from '@rocco/ui/tile-article/view-models/article';
import type { TileProps } from '@modules/articles/type';

/** The editorial card treatments: category grid and directory grid. */
export type EditorialTileVariant = 'editorial' | 'editorialLarge';

/**
 * What the article loaders hand the editorial components.
 *
 * `TileProps` rather than `ReturnType<typeof mapArticleDataToTile>`: the mapper
 * is stricter than the loaders declare themselves to be (it always produces a
 * `category`, the loader contract says it may be absent), so deriving from it
 * makes the loader output un-assignable to its own consumers.
 */
export type ArchizeenTile = TileProps;

/**
 * Whether an article should carry the film treatment on its tile.
 *
 * The legacy Latest Films tile's own condition: the banner is flagged a video
 * AND a video URL is actually on it. The flag alone is set on articles whose
 * URL never landed, and those play nothing — which is why the legacy tile
 * checks both before it shows a play control.
 *
 * Deliberately not HasVideos, which the `With video` facet narrows on. That
 * field counts video anywhere in the article and is unset on a third of the
 * Films category, so it disagrees with this in both directions.
 */
export const isFilmTile = (tile: ArchizeenTile): boolean =>
    Boolean(tile.topBanner?.isVideoBanner && tile.topBanner.videoUrl);

/**
 * Maps the tile shape the article loaders already produce onto the editorial
 * view model.
 *
 * Built on the existing loader output rather than a new GraphQL fragment: the
 * revamp is a UI change, so it must not alter what the loaders fetch. It also
 * means the admin affordances the legacy tile carries (bmEditorLink, canEdit,
 * the editable items) survive untouched on the revamped card.
 */
export const mapArchizeenTileToEditorial = (
    tile: ArchizeenTile,
    // Any card treatment the tile supports, not just the editorial pair: the
    // recently-viewed strip deals the standard card below md.
    {
        variant = 'editorial',
    }: { variant?: ArticleTileViewModel['variant'] } = {},
): ArticleTileViewModel => ({
    id: tile.ID,
    title: tile.title,
    ...(tile.introText && { introText: tile.introText }),
    // A film's card links to the article, not to `?video=1`.
    //
    // That parameter used to mean "start the banner playing where it sits",
    // which was a reasonable thing to follow a play control with. It now opens
    // the article's photo viewer full screen, so stamping it on every film card
    // put a reader who clicked a headline into a modal over a page they had not
    // seen yet. The card's play control says the article is a film; the play
    // control on the article starts it.
    //
    // `?video=1` still works for anyone who arrives on one deliberately.
    link: tile.link,
    image: { src: tile.image, alt: tile.title },
    // Left empty on purpose: the editorial card shows a play affordance over a
    // still rather than autoplaying video behind the title.
    video: [],
    hasVideo: isFilmTile(tile),
    date: 'date' in tile && tile.date ? tile.date : '',
    readTime: tile.readTime,
    label: tile.label,
    // Both are optional on the loader's tile: an article filed under no
    // category has neither, and the card renders the empty string as no label
    // rather than the word "undefined".
    category: tile.category ?? '',
    categoryLabel: tile.categoryLabel,
    professional: {
        id: tile.professionalID,
        name: tile.professionalName ?? '',
        link: tile.professionalLink,
    },
    website: tile.professionalWebsite,
    homePinned: tile.homePinned ?? false,
    isPremium: tile.showPremiumBanner ?? false,
    badge: null,
    variant,
});
